import type { JSX, RefObject } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import { SettingsPopover } from "app/components/AddonsSettingsPopover/SettingsPopover";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import useLocalStorage from "app/custom-hooks/useLocalStorage";
import { GrowthbookService, TestsNames } from "app/services/trackingServices/GrowthBookService";
import { TestValues } from "app/services/trackingServices/types";
import { getDaysSinceStoredDate } from "app/UtilitiesAndConstants/UtilityFunctions/getDaysSinceStoredDate";
import { getDateAsText } from "app/UtilitiesAndConstants/UtilityFunctions/timeAndDate";

interface IPopoverTestValue {
    value: string;
    displayIntervalDays: number;
}

interface IUseAddonsPopoverReturn {
    onClosePopover: () => void;
    renderAddonPopover: (ref: RefObject<HTMLDivElement>) => JSX.Element;
}

const localStorageKeys = {
    addonHookDismissed: "SidebarUserSettings_isAddonHookDismissed",
    addonHookDismissedDate: "SidebarUserSettings_isAddonHookDismissedDate",
};

export const useAddonsPopover = (): IUseAddonsPopoverReturn => {
    const [isOpen, setIsOpen] = useState(false);
    const { value, displayIntervalDays } = GrowthbookService.growthbookInstance.getFeatureValue(
        TestsNames.ADD_ON_TOASTER,
        { value: TestValues.CONTROL, displayIntervalDays: 0 },
    ) as unknown as IPopoverTestValue;

    const isAddonHookExperimentEnabled = value === TestValues.VARIATION;

    const { hasAddonsHub, canBuyAtLeastOneAddon } = useAddonsState();

    const [isAddonHookHookDismissed, setIsAddonHookDismissed] = useLocalStorage(localStorageKeys.addonHookDismissed);
    const [, setAddonHookDismissedDate] = useLocalStorage(localStorageKeys.addonHookDismissedDate);

    const { isPrimarySidebarOpened } = useSelector((state) => {
        return {
            isPrimarySidebarOpened: state.primaryBar?.isPrimarySidebarOpened,
        };
    });

    const isTimeToShowPopover = useMemo(() => {
        return getDaysSinceStoredDate(localStorageKeys.addonHookDismissedDate) >= displayIntervalDays;
    }, [displayIntervalDays]);

    useEffect(() => {
        if (isTimeToShowPopover) {
            setIsAddonHookDismissed("false");
        }
    }, [isTimeToShowPopover, setIsAddonHookDismissed]);

    useEffect(() => {
        const shouldDisplay =
            isAddonHookExperimentEnabled &&
            hasAddonsHub &&
            canBuyAtLeastOneAddon &&
            isAddonHookHookDismissed !== "true" &&
            !!isPrimarySidebarOpened &&
            isTimeToShowPopover;

        setIsOpen(shouldDisplay);
    }, [
        canBuyAtLeastOneAddon,
        hasAddonsHub,
        isAddonHookExperimentEnabled,
        isAddonHookHookDismissed,
        isPrimarySidebarOpened,
        isTimeToShowPopover,
    ]);

    const onClosePopover = useCallback(() => {
        setIsAddonHookDismissed("true");
        const todayAsText = getDateAsText();
        setAddonHookDismissedDate(todayAsText);
    }, [setIsAddonHookDismissed, setAddonHookDismissedDate]);

    useEffect(() => {
        if (!isPrimarySidebarOpened) {
            setIsOpen(false);
        }
    }, [isPrimarySidebarOpened]);

    const renderAddonPopover = useCallback(
        (ref: RefObject<HTMLDivElement>) => (
            <SettingsPopover
                containerRef={ref}
                isOpen={isOpen}
                onClosePopover={onClosePopover}
            />
        ),
        [isOpen, onClosePopover],
    );

    return {
        onClosePopover,
        renderAddonPopover,
    };
};
